import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Play, X, LayoutDashboard, Receipt, Users, Wallet } from "lucide-react";
import { Stat, Section, Loading, Empty, useFetch, money, dt } from "./_shared";
import { TransactionsTab, PayingCustomersTab, PlansFeesTab } from "./_finance_audit";

const RUNWAY_TONE = { green: "good", amber: "warn", red: "bad" };

const TABS = [
  ["overview", "Übersicht", LayoutDashboard],
  ["transactions", "Transaktionen", Receipt],
  ["customers", "Zahlende Kunden", Users],
  ["plans", "Pläne & Gebühren", Wallet],
];

function Overview() {
  const { data: c, loading } = useFetch("/admin/finance/cockpit");
  const { data: alertsData, reload: reloadAlerts } = useFetch("/admin/finance/alerts");
  const [busy, setBusy] = useState(false);
  if (loading) return <Loading />;
  const cash = c?.cash || {}; const rec = c?.recurring || {}; const users = c?.users || {};
  const alerts = alertsData?.alerts || [];

  const runNow = async () => { setBusy(true); try { await api.post("/admin/finance/alerts/run-now"); toast.success("Alert-Scan gestartet."); await reloadAlerts(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };
  const dismiss = async (id) => { try { await api.post(`/admin/finance/alerts/${id}/dismiss`); await reloadAlerts(); } catch (err) { toast.error(apiError(err)); } };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Stat label="Master-Cash" value={money(cash.master_cash_cents)} testId="admin-fin-cash" />
        <Stat label="VAT-Reserve" value={money(cash.vat_reserve_cents)} />
        <Stat label="Runway" value={cash.runway_months != null ? `${cash.runway_months} Mon.` : "∞"} tone={RUNWAY_TONE[cash.runway_status] || "default"} testId="admin-fin-runway" />
        <Stat label="Monatl. Burn" value={money(c?.burn_monthly_cents)} />
        <Stat label="MRR" value={money(rec.mrr_cents)} />
        <Stat label="ARR" value={money(rec.arr_cents)} />
        <Stat label="GMV 30T" value={money(c?.gmv_30d_cents)} />
        <Stat label="Zahlende Clients" value={users.paid_clients ?? 0} testId="admin-fin-paid" />
      </div>

      <Section title="Finanz-Alerts" testId="admin-fin-alerts"
        action={<Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={runNow} data-testid="admin-fin-run-alerts"><Play className="h-4 w-4" /> Jetzt prüfen</Button>}>
        {alerts.length === 0 ? <Empty text="Keine offenen Alerts." /> : (
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-3 rounded-xl border border-border bg-background p-3">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-foreground">{a.title || a.metric || "Alert"}</p><p className="truncate text-xs text-muted-foreground">{dt(a.created_at)}{a.summary ? ` · ${a.summary}` : ""}</p></div>
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => dismiss(a.id)} data-testid={`admin-fin-dismiss-${a.id}`}><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function PlansWrapper() {
  const { data: plansData, reload: reloadPlans } = useFetch("/admin/finance/plans");
  const activate = async (id) => { try { await api.post(`/admin/finance/plans/${id}/activate`); toast.success("Plan aktiviert."); await reloadPlans(); } catch (err) { toast.error(apiError(err)); } };
  return <PlansFeesTab plans={plansData?.plans || []} onActivate={activate} />;
}

export default function Finance() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Finanzen" subtitle="Cash, Runway, MRR — bis auf jede Transaktion nachvollziehbar." />

      <div className="flex flex-wrap gap-2" data-testid="admin-fin-tabs">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} type="button" onClick={() => setTab(key)} data-testid={`admin-fin-tab-${key}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${tab === key ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "transactions" && <TransactionsTab />}
      {tab === "customers" && <PayingCustomersTab />}
      {tab === "plans" && <PlansWrapper />}
    </div>
  );
}
